import { memo, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

import { cn } from "@/utils/css";
import {
    protectedRoutes,
    useIsAnalyticRoute,
    useIsBatchRoute,
    useIsBrokerAnalyticsRoute,
    useIsChatAssistantRoute,
    useIsDataRoute,
    useIsFeedbackRoute,
    useIsLoadFinderRoute,
    useIsSearchRoute,
    useLocationPotentialRouteState,
} from "@/routing";
import { Feedback, HeaderLogo, NavDropdown } from "@/components/layout";
import { setInputSearch, setSearchType, clearInputsForLoadboard, swapInputs } from "@/state/searchInputSlice";
import { clearCurrentSearch } from "@/state/currentSearchSlice";
import { SearchTab } from "@/components/common/buttons";
import { SearchInputs } from "@/components/search";
import { useTheme } from "@/components/layout/useTheme";
import { ChatAssistant, BookDemo } from "@/components/dataPages";
import { useAreFeaturesUnlocked, useCanAccessLoadFinder, useIsBroker, useCanAccessChatAssistant } from "@/auth/roles";
import { MyOffers } from "@/components/dataPages/loadboard";
import { lockedSearchInputLoadRates, lockedSearchInputMarketPotential } from "@/utils/lockedData";
import { inputsFromCurrentSearch } from "@/utils/search";

export const MainLayout = memo(({ children, searchTopRef, hideSearch = false, widget = false }) => {
    const dispatch = useDispatch();
    const { isDarkTheme } = useTheme();
    const isSearchRoute = useIsSearchRoute();
    const isFeedbackRoute = useIsFeedbackRoute();
    const isDataRoute = useIsDataRoute();
    const isAnalyticRoute = useIsAnalyticRoute();
    const isLoadFinderRoute = useIsLoadFinderRoute();
    const isChatAssistantRoute = useIsChatAssistantRoute();
    const isBatchRoute = useIsBatchRoute();
    const isBrokerAnalyticsRoute = useIsBrokerAnalyticsRoute();

    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [pageHeight, setPageHeight] = useState(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight));
    const [randomResizer, setRandomResizer] = useState(0);
    const routeSearchType = useLocationPotentialRouteState();

    const currentSearch = useSelector((state) => state.currentSearch);
    const { trailerType, pickup, delivery, pickupDate } = currentSearch;

    const hasError = useSelector((state) => state.loadRates.ratePrediction.error || state.marketPotential.error);
    const { searchType, swapPending } = useSelector((state) => state.searchInput);
    const { data: userData } = useSelector((state) => state.profile);
    const featuresLocked = !useAreFeaturesUnlocked();
    const canAccessLoadFinder = useCanAccessLoadFinder();
    const canAccessChatAssistant = useCanAccessChatAssistant();
    const isBroker = useIsBroker();
    const canAccessBatch = isBroker;

    const handleSet = () => {
        setRandomResizer(Math.random());
    };

    const debouncedResize = useMemo(() => debounce(handleSet, 300), []);

    useEffect(() => {
        setPageHeight(0);
        setTimeout(() => {
            setPageHeight(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight));
        }, 300);
    }, [location.pathname, randomResizer]);

    useEffect(() => {
        window.addEventListener("resize", debouncedResize);

        return () => {
            window.removeEventListener("resize", debouncedResize);
            debouncedResize.cancel();
        };
    }, [debouncedResize]);

    // Batch, Load finder and Analytics have no search inputs to preview, so they navigate on plain click.
    // The rest preview their inputs in place and only navigate once the user searches.
    const searchTabs = [
        { type: "load_finder", text: "Load finder", path: protectedRoutes[8].path, show: canAccessLoadFinder, navigates: true },
        { type: "load_rates", text: "Load rates", path: protectedRoutes[1].path, show: true },
        { type: "batch", text: "Batch", path: protectedRoutes[9].path, show: canAccessBatch, navigates: true },
        { type: "market_potential", text: "Market potential", path: protectedRoutes[2].path, show: true },
        { type: "loadboard", text: "Loadboard", path: protectedRoutes[6].path, show: true },
        { type: "broker_analytics", text: "Analytics", path: protectedRoutes[10].path, show: isBroker, navigates: true },
    ].filter((tab) => tab.show);

    const handleSearchTabClick = (e, tab) => {
        const { type, path, navigates } = tab;
        dispatch(setSearchType(type));

        if (navigates) {
            if (type === "load_finder") {
                // Adding trucks on Load finder is done via the plus button, not a carried-over search.
                dispatch(clearCurrentSearch());
            }
            return; // let the Link navigate
        }

        e.preventDefault();

        if (isDataRoute && type === routeSearchType && trailerType && pickup && pickupDate) {
            // Re-selecting this page's own tab restores the search that produced it.
            dispatch(setInputSearch({ trailerType, pickup, delivery, pickupDate }));
        } else if (featuresLocked && type !== "loadboard") {
            // Locked users get mocked inputs and go straight to the teaser page.
            dispatch(setInputSearch(type === "load_rates" ? lockedSearchInputLoadRates : lockedSearchInputMarketPotential));
            navigate(path);
        } else if (type === "loadboard") {
            // Loadboard searches on a different shape of input, so start it clean.
            dispatch(clearInputsForLoadboard());
        }
    };

    // Cancelling a previewed tab switch: return to this page's tab and its executed search.
    const revertToCurrentPage = () => {
        dispatch(setSearchType(routeSearchType));
        dispatch(setInputSearch(inputsFromCurrentSearch(currentSearch)));
    };

    const showLockedBanner = featuresLocked && (routeSearchType === "load_rates" || routeSearchType === "market_potential");
    const areInputsLocked = featuresLocked && (searchType === "load_rates" || searchType === "market_potential");

    return (
        <div className={cn("flex flex-col w-full items-center relative min-h-screen", { "overflow-hidden": hasError })} ref={searchTopRef}>
            <div className="flex flex-col w-full items-center justify-between relative pl-5 pr-4">
                <div className="flex flex-row w-full h-[109px] md:h-[68px] items-start md:items-center justify-between relative pt-4 md:pt-3 md:pb-3 z-header">
                    <div className="w-[195px] h-[36px] md:h-[27px] pt-[9px] md:pt-0">
                        <HeaderLogo linkEnabled={!widget} />
                    </div>
                    {(isSearchRoute || isLoadFinderRoute) && !hideSearch && !widget ? (
                        <div className="flex items-center justify-center flex-1 absolute top-[60px] md:static w-full">
                            <div className={cn("flex flex-col md:flex-row h-11")}>
                                {searchTabs.map((tab) => (
                                    <SearchTab
                                        key={tab.type}
                                        text={tab.text}
                                        to={tab.path}
                                        isActive={searchType === tab.type}
                                        onClick={(e) => handleSearchTabClick(e, tab)}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {userData && !widget ? (
                        <div className="flex items-center gap-3">
                            <NavDropdown />
                            {!isBroker && <MyOffers />}
                        </div>
                    ) : null}
                </div>
                {(isDataRoute || isAnalyticRoute || isBrokerAnalyticsRoute || isLoadFinderRoute || isBatchRoute) && !hideSearch ? (
                    <div
                        className={cn("w-full absolute z-search-overlay transition-all duration-500 ease-in-out", {
                            "opacity-100 visible": routeSearchType !== searchType,
                            "opacity-0 invisible": routeSearchType === searchType,
                            "top-[178px]": !isLoadFinderRoute && !isBatchRoute,
                            "top-[68px]": isLoadFinderRoute || isBatchRoute,
                        })}
                    >
                        <div
                            className={cn("bg-opacity-60 cursor-pointer", {
                                "bg-black": isDarkTheme,
                                "bg-[#E8EAE9]": !isDarkTheme,
                            })}
                            style={{
                                height:
                                    isLoadFinderRoute || isBatchRoute ? `calc(${pageHeight}px - 68px)` : `calc(${pageHeight}px - 178px)`,
                            }}
                            onClick={revertToCurrentPage}
                        />
                    </div>
                ) : null}
                {swapPending && !hideSearch ? (
                    <div
                        className={cn("w-full absolute z-search-overlay transition-all duration-500 ease-in-out opacity-100 visible", {
                            "top-[178px]": !isLoadFinderRoute && !isBatchRoute,
                            "top-[68px]": isLoadFinderRoute || isBatchRoute,
                        })}
                    >
                        <div
                            className={cn("bg-opacity-60 cursor-pointer", {
                                "bg-black": isDarkTheme,
                                "bg-[#E8EAE9]": !isDarkTheme,
                            })}
                            style={{
                                height:
                                    isLoadFinderRoute || isBatchRoute ? `calc(${pageHeight}px - 68px)` : `calc(${pageHeight}px - 178px)`,
                            }}
                            // Clicking the dim reverts the swap and dismisses the overlay.
                            onClick={() => dispatch(swapInputs({ pending: false }))}
                        />
                    </div>
                ) : null}
            </div>
            {!hideSearch ? (
                <>
                    {(isSearchRoute && !isLoadFinderRoute && !isBatchRoute) ||
                    (isLoadFinderRoute && searchType !== "load_finder") ||
                    (isBatchRoute && searchType !== "batch") ? (
                        <div
                            className={cn(
                                "transition-[max-height,height,opacity] duration-300 ease-out w-full max-h-[366px] opacity-100 z-search-overlay",
                                {
                                    "max-h-0 opacity-0":
                                        (isAnalyticRoute && searchType === "company_analytics") ||
                                        (isBrokerAnalyticsRoute && searchType === "broker_analytics"),
                                    "absolute top-[109px] md:top-[68px] left-0 bg-cm-p-background pointer-events-none":
                                        (isLoadFinderRoute && searchType !== "load_finder") || (isBatchRoute && searchType !== "batch"),
                                },
                            )}
                        >
                            <SearchInputs
                                isLocked={areInputsLocked}
                                className={
                                    (isLoadFinderRoute && searchType !== "load_finder") || (isBatchRoute && searchType !== "batch")
                                        ? "pointer-events-auto"
                                        : ""
                                }
                            />
                        </div>
                    ) : null}
                    <div className={cn("flex flex-col w-full", { "max-w-[1472px] p-4": !isLoadFinderRoute, "flex-1": isLoadFinderRoute })}>
                        {children}
                    </div>
                    {isFeedbackRoute && !showLockedBanner ? <Feedback /> : null}
                    {isChatAssistantRoute && canAccessChatAssistant ? <ChatAssistant /> : null}
                    {showLockedBanner ? (
                        <div className="absolute left-0 bottom-0 w-full z-10">
                            <BookDemo
                                title={routeSearchType === "market_potential" ? "Unlock Market potential" : "Unlock Load rates"}
                                description={
                                    routeSearchType === "market_potential"
                                        ? "See the real state of the market and stay on the path to success!"
                                        : "Get real-time insights, smarter load planning, and data-driven dispatching!"
                                }
                            />
                        </div>
                    ) : null}
                </>
            ) : (
                <div className="flex flex-col w-full max-w-[1472px] p-4">{children}</div>
            )}
        </div>
    );
});
